import { useState, useEffect, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useLocationsDataReady } from './useLocationsDataReady';

const LOG_PREFIX = '[ws-locations-search][useLocationsData]';

/**
 * Hook to integrate with ws-locations-data widget.
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	// Use the dedicated hook to check if LocationsData is ready
	const { isReady, error: readyError } = useLocationsDataReady();

	// Subscribe to location updates
	useEffect(() => {
		if (!isReady) {
			logger.debug(`${LOG_PREFIX} Not subscribing - isReady: ${isReady}`);
			return () => {};
		}

		const handleLocationsUpdate = () => {
			logger.debug(`${LOG_PREFIX} Received locations update`);

			const locationsData = window.DDC.LocationsData;
			const newLocations = locationsData.getLocations() || [];
			const newError = locationsData.getError();

			setLocations(newLocations);
			setError(newError);
			setIsLoading(false);
		};

		// Register for notifications
		logger.debug(`${LOG_PREFIX} Registering for location notifications`);
		const unsubscribe =
			window.DDC.LocationsData.registerLocationsNotification(
				handleLocationsUpdate
			);

		// Get initial data
		handleLocationsUpdate();

		// Cleanup subscription on unmount
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing from notifications`);
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [isReady]);

	// Function to trigger location search via ws-locations-data
	const setSearchParams = useCallback(
		(postalCode, range, excludeByRange) => {
			if (!isReady || !window.DDC?.LocationsData) {
				logger.warn(
					`${LOG_PREFIX} Cannot set postal code - LocationsData not ready`
				);
				return;
			}

			setIsLoading(true);
			setError(null);
			window.DDC.LocationsData.setSearchParams(
				postalCode,
				range,
				excludeByRange
			);
		},
		[isReady]
	);

	return {
		locations,
		locationCount: locations.length,
		error: error || readyError,
		isReady,
		isLoading: isLoading && !readyError,
		setSearchParams
	};
};
